import { useLayoutEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

// The last write that the router hasn't rendered yet, shared by every
// instance of this hook: { path, from, to } (query strings without "?").
// Two updates fired back to back from different components (e.g. a tab change
// and ListView's ?view=) both see the same render-time params; without this
// the second would rebuild from them and wipe the first. react-router's
// `setSearchParams(prev => …)` doesn't help — its `prev` is that same
// render-time snapshot. Kept router-agnostic on purpose (no window.location),
// so it behaves the same under HashRouter, BrowserRouter and MemoryRouter.
let pendingWrite = null;

// Back/Forward (popstate) or a hand-edited URL (hashchange) means the user
// left the URL that write was based on — even if the router hasn't rendered
// it yet. Without this, Back pressed before a write's render committed could
// land on the write's `from` URL and a later update would resurrect the
// abandoned write. (MemoryRouter fires neither, but has no Back button.)
if (typeof window !== "undefined") {
  const forgetPendingWrite = () => {
    pendingWrite = null;
  };
  window.addEventListener("popstate", forgetPendingWrite);
  window.addEventListener("hashchange", forgetPendingWrite);
}

// The one place that writes page state (tabs, filters, date range, sort,
// pagination, ...) into the URL so it survives reload and shared links. Build
// on this — useUrlTab is a thin wrapper over it — rather than hand-rolling
// `new URLSearchParams(searchParams)` + setSearchParams again.
//
//   const [searchParams, updateParams] = useUpdateSearchParams();
//   updateParams({ platform: "web", page: "" });           // "" / null deletes
//   updateParams({ tab: "Plans" }, { replace: true });
//   updateParams((current) => ({ tab: next(current.get("tab")) }));
//
// `updates` may be a function of the current params (including a write that
// hasn't rendered yet), for updates that depend on the existing value.
//
// History: by default each update is a new history entry, so Back steps
// through it (the analytics filters rely on this). Pass `{ replace: true }`
// for state that shouldn't pile up Back presses — tab switches do.
//
// Every caller passes string values; non-string encodings (e.g. a checkbox's
// boolean) should be converted to the string the URL should carry first.
const useUpdateSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const rendered = searchParams.toString();

  // Once a render showing the pending write (or another route) has committed,
  // the rendered URL is the truth again. Cleared after commit, not during
  // render: router location updates are transitions, so a render can be
  // thrown away — clearing there would lose a write an interrupting click
  // still needs to build on.
  useLayoutEffect(() => {
    if (
      pendingWrite &&
      (pendingWrite.path !== pathname || pendingWrite.to === rendered)
    ) {
      pendingWrite = null;
    }
  }, [pathname, rendered]);

  const updateParams = (updates, { replace = false } = {}) => {
    const base =
      pendingWrite &&
      pendingWrite.path === pathname &&
      pendingWrite.from === rendered
        ? pendingWrite.to
        : rendered;
    const next = new URLSearchParams(base);
    const changes =
      typeof updates === "function" ? updates(new URLSearchParams(base)) : updates;
    Object.entries(changes).forEach(([key, value]) => {
      if (value === "" || value == null) next.delete(key);
      else next.set(key, value);
    });
    pendingWrite = { path: pathname, from: rendered, to: next.toString() };
    setSearchParams(next, { replace });
  };

  return [searchParams, updateParams];
};

export default useUpdateSearchParams;
